<template>
  <div class="relative">
    <div v-show="!isOpen" class="note-input-collapsed">
      <Input
        readonly
        :placeholder="placeholder"
        class="h-11 rounded-xl border-gray-200 bg-white text-sm"
        @focus="open"
        @click="open"
      />
    </div>

    <Transition name="note-composer-expand">
      <div v-if="isOpen" class="note-composer overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div>
          <QuillInput
            ref="quillInputRef"
            :model-value="content"
            name="comment"
            label="About"
            :label-sr-only="true"
            @update:model-value="emit('update:content', $event)"
          />
        </div>
        <div
          class="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 md:flex-row md:items-center md:justify-between"
        >
          <SelectDropdown
            :initial-value="noteType"
            :options="noteTypeOptions"
            @selected-option="(val: NoteTypeOption) => emit('update:noteType', val)"
          />
          <div v-if="$slots.actions" class="flex gap-2 md:justify-end">
            <slot name="actions" :close="close" :open="open" />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue';
import QuillInput from '@/components/shared/QuillInput.vue';
import SelectDropdown from '@/components/shared/SelectDropdown.vue';
import { Input } from '@/components/ui/input';
import { noteTypeOptions, type NoteTypeOption } from './noteTypes';

withDefaults(
  defineProps<{
    content: string;
    noteType: NoteTypeOption;
    placeholder?: string;
  }>(),
  {
    placeholder: 'Add a note, call, or update...',
  }
);

const emit = defineEmits<{
  'update:content': [value: string];
  'update:noteType': [value: NoteTypeOption];
  open: [];
  close: [];
}>();

const isOpen = ref(false);
const quillInputRef = ref<InstanceType<typeof QuillInput>>();

const open = async () => {
  isOpen.value = true;
  emit('open');
  await nextTick();
  quillInputRef.value?.setFocus();
};

const close = () => {
  isOpen.value = false;
  emit('close');
};

defineExpose({ open, close });
</script>

<style scoped>
@reference "tailwindcss";

.note-composer:deep(label) {
  @apply hidden;
}

.note-composer:deep(#quill-wrapper) {
  @apply w-full;
}

.note-composer:deep(.ql-toolbar.ql-snow) {
  @apply w-full rounded-none border-x-0 border-t-0 border-b border-gray-200 px-4 py-3;
}

.note-composer:deep(.ql-toolbar.ql-snow .ql-formats) {
  @apply mr-3;
}

.note-composer:deep(.ql-container.ql-snow) {
  @apply min-h-32 rounded-none border-0;
}

.note-composer:deep(.ql-editor) {
  @apply min-h-28 px-4 py-3 text-sm text-gray-700;
}

.note-composer:deep(.ql-editor.ql-blank::before) {
  color: rgb(107 114 128);
  content: 'Type your note here...';
  font-style: normal;
  left: 1rem;
  right: 1rem;
}

.note-input-collapsed {
  @apply transition-opacity duration-100 ease-out;
}

.note-composer-expand-enter-active,
.note-composer-expand-leave-active {
  transition:
    opacity 180ms ease,
    transform 220ms ease,
    max-height 240ms ease;
  transform-origin: top;
  overflow: hidden;
}

.note-composer-expand-enter-from,
.note-composer-expand-leave-to {
  opacity: 0;
  transform: translateY(-4px) scaleY(0.99);
}

.note-composer-expand-enter-to,
.note-composer-expand-leave-from {
  opacity: 1;
  transform: translateY(0) scaleY(1);
}

.note-composer-expand-enter-from {
  max-height: 0;
}

.note-composer-expand-enter-to {
  max-height: 36rem;
}

.note-composer-expand-leave-from {
  max-height: 36rem;
}

.note-composer-expand-leave-to {
  max-height: 0;
}
</style>
